import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { PyvSuperEjecutivoServices } from '../core/http/pyv-super-ejecutivo/pyv-super-ejecutivo.services';
import {
  MAXIMO_CARACTERES_SUPER_EJECUTIVO,
  MENSAJES_SUPER_EJECUTIVO,
  MILISEGUNDOS_AVISO_SESION_EXPIRADA_SUPER_EJECUTIVO,
  MILISEGUNDOS_ESPERA_PROCESANDO_SUPER_EJECUTIVO,
  RUTA_CERRAR_CONVERSACION_SUPER_EJECUTIVO,
  RUTA_CHAT_SUPER_EJECUTIVO,
  RUTA_CONVERSACION_ACTUAL_SUPER_EJECUTIVO,
} from './super-ejecutivo.constants';
import { ICuerpoChatSuperEjecutivo, IConversacionActualSuperEjecutivo, IMensajeSuperEjecutivo, TamanoPanelSuperEjecutivo } from './super-ejecutivo.interface';
import {
  aplicarEventoSuperEjecutivo,
  marcarPropuestasSuperEjecutivo,
  mensajesDesdeHistorialSuperEjecutivo,
  propuestasPendientesSuperEjecutivo,
  rechazarPendientesSuperEjecutivo,
  regresarAPendientesSuperEjecutivo,
  separarEventosSse,
  terminaConSesionExpiradaSuperEjecutivo,
} from './super-ejecutivo-stream.util';

/**
 * Conversación con el Super Ejecutivo y estado del panel flotante (requerimiento `kia-en-kobra-admin`). Vive en la
 * raíz para que el panel conserve la conversación, su tamaño y lo escrito al navegar (RN6, RN7).
 * Kobra Admin habla con el Super Ejecutivo a través del módulo super-ejecutivo de pyv-services (RN23).
 */
@Injectable({ providedIn: 'root' })
export class SuperEjecutivoService {
  private readonly pyv = inject(PyvSuperEjecutivoServices);

  /** Mensajes del panel; la respuesta en curso es el último. */
  public readonly mensajes = signal<IMensajeSuperEjecutivo[]>([]);
  /** El Super Ejecutivo está respondiendo (RN12). */
  public readonly procesando = signal(false);
  /** Qué está haciendo el Super Ejecutivo, en lenguaje de negocio (RN10). */
  public readonly avance = signal<string | null>(null);
  /** Error que se muestra en el panel (RN28). */
  public readonly error = signal<string | null>(null);
  /** El error se puede reintentar: hay una acción guardada para repetir (RN28). */
  public readonly puedeReintentar = signal(false);
  /** Panel abierto y su tamaño (RN4-RN6). */
  public readonly abierto = signal(false);
  public readonly tamano = signal<Exclude<TamanoPanelSuperEjecutivo, 'completo'>>('moderado');
  /** Lo que el usuario está escribiendo; se conserva al cerrar el panel o navegar (RN5.1). */
  public readonly borrador = signal('');
  /** RN8.2: el Super Ejecutivo terminó una respuesta con el panel cerrado. */
  public readonly respuestaNueva = signal(false);
  /** RN8.2: hay propuestas esperando decisión. */
  public readonly propuestaPendiente = computed(() => propuestasPendientesSuperEjecutivo(this.mensajes()).length > 0);

  private conversationId: string | null = null;
  /** Decisiones de las propuestas de la respuesta actual que todavía no se envían (RN15). */
  private readonly decisiones = new Map<string, boolean>();
  /** La acción que falló, para reintentarla tal cual (RN28). */
  private reintento: (() => Promise<void>) | null = null;
  private cargada = false;
  /** Este panel tiene un turno en curso (no uno empezado antes de recargar). */
  private turnoEnCurso = false;
  /** Cambia al reiniciar: un turno de antes ya no toca el estado nuevo. */
  private generacion = 0;
  /** Ids de los mensajes del panel. `crypto.randomUUID()` no existe fuera de HTTPS (local corre por HTTP). */
  private secuencia = 0;
  private espera: ReturnType<typeof setTimeout> | null = null;

  /** Abre el panel y, la primera vez, recupera la última conversación (RN8.1). */
  public abrir(): void {
    this.abierto.set(true);
    this.respuestaNueva.set(false);
    void this.cargarConversacion();
  }

  public cerrar(): void {
    this.abierto.set(false);
  }

  public alternarTamano(): void {
    this.tamano.update((t) => (t === 'moderado' ? 'agrandado' : 'moderado'));
  }

  /**
   * Recupera la última conversación vigente del usuario (RN8.1). Si el Super Ejecutivo sigue respondiendo (el usuario
   * recargó a la mitad), vuelve a consultar hasta que termine; al terminar con el panel cerrado, prende el
   * indicador de respuesta nueva (RN8.2).
   */
  public async cargarConversacion(forzar = false): Promise<void> {
    if ((this.cargada && !forzar) || this.turnoEnCurso) {
      return;
    }

    this.cargada = true;
    const esperabaRespuesta = forzar && this.procesando();

    try {
      const actual = await firstValueFrom(
        this.pyv
          .get<IConversacionActualSuperEjecutivo>(RUTA_CONVERSACION_ACTUAL_SUPER_EJECUTIVO)
          .pipe(map((res) => res.data)),
      );

      this.conversationId = actual?.conversationId ?? null;
      this.mensajes.set(mensajesDesdeHistorialSuperEjecutivo(actual?.historial ?? []));
      this.procesando.set(Boolean(actual?.procesando));

      if (actual?.procesando) {
        this.avance.set(MENSAJES_SUPER_EJECUTIVO.PENSANDO);
        this.espera = setTimeout(() => {
          this.espera = null;
          void this.cargarConversacion(true);
        }, MILISEGUNDOS_ESPERA_PROCESANDO_SUPER_EJECUTIVO);

        return;
      }

      this.avance.set(null);

      if (esperabaRespuesta && !this.abierto()) {
        this.respuestaNueva.set(true);
      }
    } catch {
      // Sin la conversación anterior el panel funciona igual: el siguiente mensaje empieza una.
      this.cargada = false;
      this.procesando.set(false);
      this.avance.set(null);
    }
  }

  /** Envía un mensaje (RN9, RN13). Si había propuestas sin contestar quedan rechazadas (RN17). */
  public async enviarMensaje(texto: string): Promise<void> {
    const mensaje = texto.trim();

    if (!mensaje || this.procesando() || mensaje.length > MAXIMO_CARACTERES_SUPER_EJECUTIVO) {
      return;
    }

    this.borrador.set('');
    this.decisiones.clear();
    this.mensajes.update((ms) => [...rechazarPendientesSuperEjecutivo(ms), this.nuevoMensaje('usuario', mensaje)]);
    await this.turno({ conversationId: this.conversationId ?? undefined, message: mensaje });
  }

  /**
   * Decisión del usuario sobre una propuesta (RN14-RN16). Con varias propuestas en la respuesta, se
   * envían juntas cuando todas tienen decisión: el Super Ejecutivo toma como rechazada la que no la tenga.
   * Si el envío no llega al Super Ejecutivo, las propuestas regresan a pendientes (ver `manejarError`).
   */
  public async decidirPropuesta(approvalId: string, aprobada: boolean): Promise<void> {
    if (this.procesando() || !this.conversationId) {
      return;
    }

    this.decisiones.set(approvalId, aprobada);
    this.mensajes.update((ms) => marcarPropuestasSuperEjecutivo(ms, new Map([[approvalId, aprobada]])));

    if (propuestasPendientesSuperEjecutivo(this.mensajes()).length > 0) {
      return;
    }

    const approvals = [...this.decisiones].map(([id, approved]) => ({ approvalId: id, approved }));
    this.decisiones.clear();
    await this.turno({ conversationId: this.conversationId, approvals });
  }

  /** "Nueva conversación" (RN8): el Super Ejecutivo cierra la actual y descarta sus propuestas pendientes. */
  public async nuevaConversacion(): Promise<void> {
    if (this.procesando()) {
      return;
    }

    this.limpiarError();

    try {
      await firstValueFrom(this.pyv.post(RUTA_CERRAR_CONVERSACION_SUPER_EJECUTIVO, {}));
      this.conversationId = null;
      this.decisiones.clear();
      this.mensajes.set([]);
    } catch (error) {
      const mensaje = typeof error === 'string' && error ? error : MENSAJES_SUPER_EJECUTIVO.SUPER_EJECUTIVO_NO_DISPONIBLE;
      this.fallar(mensaje, () => this.nuevaConversacion());
    }
  }

  /**
   * Olvida la conversación y el estado del panel. Al cerrar sesión el layout se destruye, pero este
   * servicio sigue vivo: sin esto, quien entre después vería la conversación del usuario anterior.
   */
  public reiniciar(): void {
    if (this.espera) {
      clearTimeout(this.espera);
      this.espera = null;
    }

    this.generacion++;
    this.turnoEnCurso = false;
    this.conversationId = null;
    this.decisiones.clear();
    this.cargada = false;
    this.limpiarError();
    this.mensajes.set([]);
    this.procesando.set(false);
    this.avance.set(null);
    this.abierto.set(false);
    this.tamano.set('moderado');
    this.borrador.set('');
    this.respuestaNueva.set(false);
  }

  /** Repite la acción que falló: el último envío, la consulta de la conversación o "Nueva conversación" (RN28). */
  public async reintentar(): Promise<void> {
    const reintento = this.reintento;

    if (reintento && !this.procesando()) {
      this.limpiarError();
      await reintento();
    }
  }

  /** Un turno del Super Ejecutivo: lee su stream y actualiza la respuesta conforme llega (RN9, RN10, RN26). */
  private async turno(cuerpo: ICuerpoChatSuperEjecutivo): Promise<void> {
    const generacion = this.generacion;
    this.turnoEnCurso = true;
    this.limpiarError();
    this.avance.set(MENSAJES_SUPER_EJECUTIVO.PENSANDO);
    this.procesando.set(true);
    const respuesta = this.nuevoMensaje('superEjecutivo', '');
    this.mensajes.update((ms) => [...ms, respuesta]);

    let respondio = false;

    try {
      const response = await this.pyv.postStream(RUTA_CHAT_SUPER_EJECUTIVO, cuerpo);
      respondio = true;

      if (!response.ok || !response.body) {
        this.quitarMensaje(respuesta.id);
        await this.manejarError(response, cuerpo);

        return;
      }

      await this.leerRespuesta(response.body, respuesta.id, generacion);
      this.revisarSesionExpirada(respuesta.id);
    } catch {
      this.fallarSinConexion(cuerpo, respondio);
    } finally {
      if (generacion === this.generacion) {
        this.terminarTurno(respuesta.id);
      }
    }
  }

  /**
   * Se perdió la conexión (RN28). Con un mensaje, reintentar lo reenvía. Con decisiones sobre propuestas (RN16):
   * - si nunca hubo respuesta, la decisión no llegó: las propuestas regresan a pendientes para decidirlas otra vez;
   * - si se cortó a la mitad de la respuesta, la decisión sí llegó y pudo ejecutarse: reintentar vuelve a consultar
   *   la conversación para mostrar lo que realmente pasó.
   */
  private fallarSinConexion(cuerpo: ICuerpoChatSuperEjecutivo, respondio: boolean): void {
    if (!('approvals' in cuerpo)) {
      this.fallar(MENSAJES_SUPER_EJECUTIVO.SUPER_EJECUTIVO_NO_DISPONIBLE, () => this.turno(cuerpo));

      return;
    }

    if (respondio) {
      this.fallar(MENSAJES_SUPER_EJECUTIVO.SUPER_EJECUTIVO_NO_DISPONIBLE, () => this.cargarConversacion(true));

      return;
    }

    this.mensajes.update((ms) => regresarAPendientesSuperEjecutivo(ms, cuerpo.approvals.map((a) => a.approvalId)));
    this.fallar(MENSAJES_SUPER_EJECUTIVO.DECISION_NO_ENVIADA, null);
  }

  private terminarTurno(id: string): void {
    this.turnoEnCurso = false;
    this.procesando.set(false);
    this.avance.set(null);
    this.quitarSiVacio(id);

    if (!this.abierto()) {
      this.respuestaNueva.set(true);
    }
  }

  private async leerRespuesta(body: ReadableStream<Uint8Array>, id: string, generacion: number): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let pendiente = '';

    for (;;) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      // Se cerró sesión mientras el Super Ejecutivo respondía: termina por su cuenta; aquí ya no se muestra.
      if (generacion !== this.generacion) {
        await reader.cancel();

        return;
      }

      const { eventos, resto } = separarEventosSse(pendiente + decoder.decode(value, { stream: true }));
      pendiente = resto;

      for (const evento of eventos) {
        this.mensajes.update((ms) =>
          ms.map((m) => {
            if (m.id !== id) {
              return m;
            }

            const aplicado = aplicarEventoSuperEjecutivo(m, evento);

            if (aplicado.conversationId) {
              this.conversationId = aplicado.conversationId;
            }

            if (aplicado.avance !== undefined) {
              this.avance.set(aplicado.avance);
            }

            return aplicado.mensaje;
          }),
        );
      }
    }
  }

  /**
   * Errores antes del stream: sesión expirada (RN21), conversación expirada (RN29), ocupada (RN12), sin permiso o
   * el Super Ejecutivo caído (RN28). Si eran decisiones sobre propuestas, el Super Ejecutivo no las recibió: vuelven
   * a pendientes para decidirlas otra vez, salvo si la conversación expiró (ya no se pueden ejecutar).
   */
  private async manejarError(response: Response, cuerpo: ICuerpoChatSuperEjecutivo): Promise<void> {
    const mensaje = await this.mensajeDeError(response);
    const decididas = 'approvals' in cuerpo ? cuerpo.approvals.map((a) => a.approvalId) : [];

    if (response.status === 401) {
      this.avisarSesionExpirada();

      return;
    }

    if (response.status === 404) {
      this.conversationId = null;

      if ('message' in cuerpo) {
        this.agregarAviso(MENSAJES_SUPER_EJECUTIVO.CONVERSACION_EXPIRADA);
        await this.turno({ message: cuerpo.message });

        return;
      }

      this.mensajes.update((ms) => marcarPropuestasSuperEjecutivo(ms, new Map(decididas.map((id) => [id, false]))));
      this.agregarAviso(MENSAJES_SUPER_EJECUTIVO.PROPUESTA_NO_EJECUTADA_CONVERSACION_EXPIRADA);

      return;
    }

    this.mensajes.update((ms) => regresarAPendientesSuperEjecutivo(ms, decididas));

    if (response.status === 409) {
      this.agregarAviso(MENSAJES_SUPER_EJECUTIVO.CONVERSACION_OCUPADA);

      return;
    }

    if (response.status >= 400 && response.status < 500) {
      this.agregarAviso(mensaje ?? MENSAJES_SUPER_EJECUTIVO.SUPER_EJECUTIVO_NO_DISPONIBLE);

      return;
    }

    // Con decisiones no se repite el envío: las propuestas ya regresaron a pendientes y se deciden otra vez.
    this.fallar(MENSAJES_SUPER_EJECUTIVO.SUPER_EJECUTIVO_NO_DISPONIBLE, 'message' in cuerpo ? () => this.turno(cuerpo) : null);
  }

  /**
   * RN21: la sesión expiró a la mitad de la respuesta (el Super Ejecutivo lo dice al final de su texto). Lo que
   * alcanzó a llegar ya se ve; después sigue el flujo normal de sesión expirada.
   */
  private revisarSesionExpirada(id: string): void {
    const respuesta = this.mensajes().find((m) => m.id === id);

    if (respuesta && terminaConSesionExpiradaSuperEjecutivo(respuesta.texto, MENSAJES_SUPER_EJECUTIVO.SESION_EXPIRADA)) {
      this.programarCierreDeSesion();
    }
  }

  /** RN21: avisa en el panel que la sesión expiró y después sigue el flujo normal de Kobra Admin (login). */
  private avisarSesionExpirada(): void {
    this.agregarAviso(MENSAJES_SUPER_EJECUTIVO.SESION_EXPIRADA);
    this.programarCierreDeSesion();
  }

  private programarCierreDeSesion(): void {
    setTimeout(() => this.pyv.cerrarSesion(), MILISEGUNDOS_AVISO_SESION_EXPIRADA_SUPER_EJECUTIVO);
  }

  /** Mensaje de error de pyv-services (`{ error: "<texto>" }`). */
  private async mensajeDeError(response: Response): Promise<string | null> {
    try {
      const cuerpo = (await response.json()) as { error?: unknown };

      return typeof cuerpo.error === 'string' && cuerpo.error.trim() ? cuerpo.error : null;
    } catch {
      return null;
    }
  }

  private fallar(mensaje: string, reintento: (() => Promise<void>) | null): void {
    this.error.set(mensaje);
    this.reintento = reintento;
    this.puedeReintentar.set(reintento !== null);
  }

  private limpiarError(): void {
    this.error.set(null);
    this.reintento = null;
    this.puedeReintentar.set(false);
  }

  private agregarAviso(texto: string): void {
    this.mensajes.update((ms) => [...ms, this.nuevoMensaje('aviso', texto)]);
  }

  private nuevoMensaje(rol: IMensajeSuperEjecutivo['rol'], texto: string): IMensajeSuperEjecutivo {
    this.secuencia++;

    return { id: `mensaje-${this.secuencia}`, rol, texto, propuestas: [] };
  }

  private quitarMensaje(id: string): void {
    this.mensajes.update((ms) => ms.filter((m) => m.id !== id));
  }

  private quitarSiVacio(id: string): void {
    this.mensajes.update((ms) =>
      ms.filter((m) => m.id !== id || m.texto.trim() !== '' || m.propuestas.length > 0),
    );
  }
}
